/**
 * Campaign Service
 * Handles all campaign-related API calls
 */

import { apiService } from './api'

export interface Campaign {
  id: string
  name: string
  description?: string
  status?: 'draft' | 'active'
  channel?: string
  customerType?: string
  indication?: string
  therapeuticArea?: string
  brand?: string
  product?: string
  fdaFilingNumber?: string
  dofuExpiration?: string
  createdAt: string
  updatedAt: string
  userId: string
}

export interface CampaignAsset {
  id: string
  campaignId: string
  name: string
  type: string
  url?: string
  thumbnailUrl?: string
  metadata?: any
  uploadedAt: string
}

export interface CampaignConcept {
  campaignId: string
  name?: string
  description?: string
  targetAudience?: string
  keyMessage?: string
  creativeDirection?: string
  updatedAt: string
}

export interface CampaignResults {
  campaignId: string
  complianceScore?: number
  violations?: any[]
  recommendations?: string[]
  processedAt: string
}

class CampaignService {
  /**
   * Get all campaigns for the current user
   */
  async getCampaigns(): Promise<Campaign[]> {
    try {
      const response = await apiService.request('/api/campaigns')
      if (response.success && response.data?.campaigns) {
        return response.data.campaigns
      }
      return []
    } catch (error) {
      console.error('Failed to get campaigns:', error)
      return []
    }
  }

  /**
   * Get a single campaign by ID
   */
  async getCampaign(campaignId: string): Promise<Campaign | null> {
    try {
      const response = await apiService.request(`/api/campaigns/${campaignId}`)
      if (response.success && response.data?.campaign) {
        return response.data.campaign
      }
      return null
    } catch (error) {
      console.error('Failed to get campaign:', error)
      return null
    }
  }

  /**
   * Create a new campaign
   */
  async createCampaign(campaign: Partial<Campaign>): Promise<{ success: boolean; campaign?: Campaign; error?: string }> {
    try {
      const response = await apiService.request('/api/campaigns', {
        method: 'POST',
        body: JSON.stringify({ campaign })
      })
      
      if (response.success && response.data?.campaign) {
        return { success: true, campaign: response.data.campaign }
      }
      
      return { success: false, error: response.error || 'Failed to create campaign' }
    } catch (error) {
      console.error('Failed to create campaign:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Failed to create campaign' }
    }
  }

  /**
   * Update an existing campaign
   */
  async updateCampaign(campaignId: string, updates: Partial<Campaign>): Promise<{ success: boolean; campaign?: Campaign; error?: string }> {
    try {
      const response = await apiService.request(`/api/campaigns/${campaignId}`, {
        method: 'PUT',
        body: JSON.stringify({ updates })
      })
      
      if (response.success && response.data?.campaign) {
        return { success: true, campaign: response.data.campaign }
      }
      
      return { success: false, error: response.error || 'Failed to update campaign' }
    } catch (error) {
      console.error('Failed to update campaign:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update campaign' }
    }
  }

  /**
   * Delete a campaign
   */
  async deleteCampaign(campaignId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await apiService.request(`/api/campaigns/${campaignId}`, {
        method: 'DELETE'
      })
      
      return { success: response.success, error: response.error }
    } catch (error) {
      console.error('Failed to delete campaign:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Failed to delete campaign' }
    }
  }

  /**
   * Add an asset to a campaign
   */
  async addAssetToCampaign(campaignId: string, asset: any): Promise<{ success: boolean; asset?: CampaignAsset; error?: string }> {
    try {
      const response = await apiService.request(`/api/campaigns/${campaignId}/assets`, {
        method: 'POST',
        body: JSON.stringify({ asset })
      })
      
      if (response.success && response.data?.asset) {
        return { success: true, asset: response.data.asset }
      }
      
      return { success: false, error: response.error || 'Failed to add asset' }
    } catch (error) {
      console.error('Failed to add asset to campaign:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Failed to add asset' }
    }
  }

  /**
   * Get all assets for a campaign
   */
  async getCampaignAssets(campaignId: string): Promise<CampaignAsset[]> {
    try {
      const response = await apiService.request(`/api/campaigns/${campaignId}/assets`)
      if (response.success && response.data?.assets) {
        // Fix: API returns assets as {asset: {...}}, so flatten them
        const flattened = response.data.assets.map((item: any) => {
          // If asset is nested, flatten it
          if (item.asset) {
            return item.asset
          }
          // Otherwise return as-is
          return item
        })
        console.log(`✅ Loaded ${flattened.length} campaign assets`)
        return flattened
      }
      console.warn('No assets in response:', response)
      return []
    } catch (error) {
      console.error('Failed to get campaign assets:', error)
      return []
    }
  }

  /**
   * Save campaign concept
   */
  async saveCampaignConcept(campaignId: string, concept: Partial<CampaignConcept>): Promise<{ success: boolean; concept?: CampaignConcept; error?: string }> {
    try {
      const response = await apiService.request(`/api/campaigns/${campaignId}/concept`, {
        method: 'PUT',
        body: JSON.stringify({ concept })
      })
      
      if (response.success && response.data?.concept) {
        return { success: true, concept: response.data.concept }
      }
      
      return { success: false, error: response.error || 'Failed to save concept' }
    } catch (error) {
      console.error('Failed to save campaign concept:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Failed to save concept' }
    }
  }

  /**
   * Get campaign concept
   */
  async getCampaignConcept(campaignId: string): Promise<CampaignConcept | null> {
    try {
      const response = await apiService.request(`/api/campaigns/${campaignId}/concept`)
      if (response.success && response.data?.concept) {
        return response.data.concept
      }
      return null
    } catch (error) {
      console.error('Failed to get campaign concept:', error)
      return null
    }
  }

  /**
   * Save campaign results
   */
  async saveCampaignResults(campaignId: string, results: Partial<CampaignResults>): Promise<{ success: boolean; results?: CampaignResults; error?: string }> {
    try {
      const response = await apiService.request(`/api/campaigns/${campaignId}/results`, {
        method: 'POST',
        body: JSON.stringify({ results })
      })
      
      if (response.success && response.data?.results) {
        return { success: true, results: response.data.results }
      }
      
      return { success: false, error: response.error || 'Failed to save results' }
    } catch (error) {
      console.error('Failed to save campaign results:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Failed to save results' }
    }
  }

  /**
   * Get campaign results
   */
  async getCampaignResults(campaignId: string): Promise<CampaignResults | null> {
    try {
      const response = await apiService.request(`/api/campaigns/${campaignId}/results`)
      if (response.success && response.data?.results) {
        return response.data.results
      }
      return null
    } catch (error) {
      console.error('Failed to get campaign results:', error)
      return null
    }
  }
}

// Export singleton instance
export const campaignService = new CampaignService()